/**
 * Entity URL utilities — single source of truth for generating navigation URLs
 * from entity types/refs.
 *
 * The canonical entity → URL map is generated from `shared/config/modules_entities.config.json`
 * into `apps/frontend/src/generated/entity-urls.ts`. The matching Python registry is at
 * `shared/generated/entity_urls.py`. Both are regenerated by the pre-commit hook when
 * the config changes — never edit the generated files by hand.
 *
 * Two entry points:
 * - getEntityUrl(): for resolved entities (entity_type + entity_id + optional context)
 * - getEntityUrlFromRef(): for raw entity refs ("table_name:uuid") from MCP tool calls
 *
 * Both look up `entityType` in the generated registry. The registry supports
 * a `urlContextTemplate` (used when extra context like board_id is available);
 * the resolver prefers it when fillable, else falls back to `urlTemplate`.
 *
 * A small convention-based fallback handles entities that lack a registry entry —
 * keeps tool-call chips usable for new entities before they're added to the config.
 */
import {
  ENTITY_URL_REGISTRY,
  fillTemplate,
  resolveEntityUrl,
} from '../../generated/entity-urls';
import {
  getEntityByTable,
  getEntityByTableSuffix,
  MODULE_KEY_TO_BASE_PATH,
} from '../config/modules.config';

// ---------- types ----------

export interface EntityUrlOptions {
  entityType: string;
  entityId: string;
  boardId?: string;
  linkToFullPage?: boolean;
  /** Extra context for nested entities */
  context?: Record<string, string>;
  /**
   * Skip the convention-based fallback — resolve from the registry or return null.
   *
   * The fallback is a best-effort guess that keeps tool-call chips usable for
   * entities not yet in the config. A caller rendering a link for a *persisted*
   * `entity_type` (notification inbox rows, which include legacy bare nouns from
   * before the emit sites named real tables) wants no link at all rather than a
   * plausible-but-wrong one that 404s or lands on someone else's page.
   */
  registryOnly?: boolean;
}

// ---------- convention fallback ----------

// Modules with layout wrappers where entity detail routes include the entity type
// segment even when it matches the module name (e.g. /tasks/tasks/:id, not /tasks/:id).
const NESTED_ENTITY_MODULES = new Set(['tasks']);

/**
 * Derive a URL from the module/entity naming convention for entity types that
 * don't have an explicit registry entry. Convention: "module_entity" → /basePath/entity/:id.
 */
function getUrlByConvention(entityType: string, entityId: string): string | null {
  for (const [moduleKey, basePath] of MODULE_KEY_TO_BASE_PATH) {
    if (entityType.startsWith(moduleKey + '_')) {
      const entitySegment = entityType.substring(moduleKey.length + 1);
      if (entitySegment === moduleKey && !NESTED_ENTITY_MODULES.has(moduleKey)) {
        return `/${basePath}/${entityId}`;
      }
      return `/${basePath}/${entitySegment}/${entityId}`;
    }
    if (entityType === moduleKey) {
      return `/${basePath}/${entityId}`;
    }
  }

  const entityInfo = getEntityByTable(entityType) ?? getEntityByTableSuffix(entityType);
  if (entityInfo) {
    const modulePath = entityInfo.module.basePaths[0]?.replace(/^\//, '') ?? entityInfo.module.id;
    if (entityType === modulePath || entityType === entityInfo.module.id) {
      return `/${modulePath}/${entityId}`;
    }
    return `/${modulePath}/${entityType}/${entityId}`;
  }

  return null;
}

// ---------- public API ----------

/**
 * Generate a URL path for navigating to a specific entity.
 * Used by EntityChip and anywhere we have a resolved entity_type + entity_id.
 */
export function getEntityUrl(options: EntityUrlOptions): string | null {
  const { entityType, entityId, boardId, linkToFullPage, context, registryOnly } = options;

  const entry = ENTITY_URL_REGISTRY[entityType];
  if (entry) {
    const values: Record<string, string | undefined> = {
      id: entityId,
      ...(context ?? {}),
      ...(boardId ? { board_id: boardId } : {}),
    };

    // Prefer the contextual template when its placeholders can be filled and
    // the caller hasn't explicitly asked for the full-page URL.
    if (entry.urlContextTemplate && !linkToFullPage) {
      const ctxUrl = fillTemplate(entry.urlContextTemplate, values);
      if (ctxUrl) return ctxUrl;
    }

    return fillTemplate(entry.urlTemplate, values);
  }

  return registryOnly ? null : getUrlByConvention(entityType, entityId);
}

/**
 * Narrow a loose payload (a notification's `data`) to usable URL-template context.
 *
 * A nested entity's `urlContextTemplate` needs more than `{id}` — a chatbot
 * channel needs `bot_id`, an agent approval needs `agent_id` — and emit sites
 * already carry those. Only string values are eligible, and `id` is dropped:
 * `getEntityUrl` spreads context OVER `{id}`, so a payload that happened to carry
 * its own `id` would silently retarget the link away from the actual entity.
 *
 * Mirrors `NotificationEmailService._url_context` (shared/notifications/email_delivery.py)
 * so the email CTA and the in-app surfaces resolve the same destination.
 */
export function entityUrlContext(
  data: Record<string, unknown> | null | undefined,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(data ?? {}).filter(
      (entry): entry is [string, string] => entry[0] !== 'id' && typeof entry[1] === 'string',
    ),
  );
}

/**
 * Resolve the list-page URL for an entity type from the URL registry.
 *
 * The registry stores per-entity *detail* templates (e.g.
 * `/master-data/organizations/{id}`). A list page is that template with the
 * trailing `/{id}` segment removed. Returns null when the entity type has no
 * registry entry or its detail template isn't a plain `/path/{id}` shape
 * (e.g. query-param routes like `/ai-chat?session={id}`).
 *
 * Used by tag chips to navigate to a tag-filtered list page.
 */
export function getEntityListUrl(entityType: string): string | null {
  const entry = ENTITY_URL_REGISTRY[entityType];
  if (!entry) return null;
  const template = entry.urlTemplate;
  if (!template.endsWith('/{id}')) return null;
  return template.slice(0, -'/{id}'.length);
}

/**
 * Resolve the URL of a list page filtered by one or more tags.
 * Returns null when the entity type has no derivable list URL.
 */
export function getTagFilteredListUrl(
  entityType: string,
  tagIds: string[],
): string | null {
  const listUrl = getEntityListUrl(entityType);
  if (!listUrl || tagIds.length === 0) return null;
  return `${listUrl}?tags=${tagIds.map(encodeURIComponent).join(',')}`;
}

/**
 * Generate a URL from a raw entity ref string ("table_name:uuid").
 * Used by tool call entity chips in chat.
 */
export function getEntityUrlFromRef(ref: string, context?: Record<string, string>): string | null {
  const colonIdx = ref.indexOf(':');
  if (colonIdx === -1) return null;

  const entityType = ref.substring(0, colonIdx);
  const entityId = ref.substring(colonIdx + 1);
  if (!entityType || !entityId) return null;

  return getEntityUrl({ entityType, entityId, context });
}

/**
 * Navigate to an entity's detail page using the URL registry.
 *
 * Preferred over `navigate(\`/sales/deals/${id}\`)` — keeps entity URL shapes
 * in one place (`shared/config/modules_entities.config.json`) and stays in
 * sync if a route ever moves.
 *
 * Usage:
 *   navigateToEntity(navigate, 'sales_deals', deal.id);
 *   navigateToEntity(navigate, 'sales_deals', deal.id, { replace: true });
 *   navigateToEntity(navigate, 'tasks_tasks', task.id, { context: { board_id } });
 */
export function navigateToEntity(
  navigate: (to: string, options?: { replace?: boolean; state?: unknown }) => void,
  entityType: string,
  entityId: string,
  options?: {
    replace?: boolean;
    state?: unknown;
    context?: Record<string, string>;
    linkToFullPage?: boolean;
  },
): void {
  const url = getEntityUrl({
    entityType,
    entityId,
    context: options?.context,
    linkToFullPage: options?.linkToFullPage,
  });
  if (url) {
    navigate(url, { replace: options?.replace, state: options?.state });
  }
}

// Re-export the resolver for callers that work directly with arbitrary key/value contexts.
export { resolveEntityUrl, ENTITY_URL_REGISTRY };
